import { normalizeAccountIdFromIdentityAri } from '@atlassian/conversation-assistant-utils/default-agent';

import type { FollowUpSendAgentIdentity, StoreAssistant } from './types';

const normalizeString = (value: unknown): string | undefined =>
	typeof value === 'string' && value.trim() ? value.trim() : undefined;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

/**
 * Derives the agent that authored an assistant turn for a follow-up send.
 *
 * `author.id` is the sole canonical routing source. The nested message must be
 * an assistant turn because human `author.id` values are account IDs and must
 * never be sent as agent routing. `author.ari` is retained only for optional
 * frontend display/name hydration. Agent-author metadata, named IDs, and
 * external configuration references are intentionally ignored. Missing or
 * malformed routing metadata fails closed instead of falling back to another
 * agent.
 */
export const deriveFollowUpSendAgentIdentity = (
	storeAssistant: StoreAssistant,
): FollowUpSendAgentIdentity | undefined => {
	if (storeAssistant?.type !== 'ASSISTANT') {
		return undefined;
	}

	const message = storeAssistant?.content?.message?.message;
	if (!isRecord(message) || message.role !== 'ASSISTANT') {
		return undefined;
	}

	const author = isRecord(message.author) ? message.author : undefined;
	const canonicalAgentId = normalizeString(author?.id);
	const identityAccountId = normalizeAccountIdFromIdentityAri(normalizeString(author?.ari));

	if (!canonicalAgentId) {
		return undefined;
	}

	return {
		id: canonicalAgentId,
		...(identityAccountId ? { identityAccountId } : {}),
	};
};
